import React, { useEffect, useMemo, useState } from 'react'
import { FormField, FormSelectField } from '@/components/Form/FormField'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { TradingAccountDto } from 'api/Clients/Types/TradingAccount'
import { NameDto } from 'api/Common/Types'
import { IBIndividualRebate } from 'api/Trading-Accounts/Types/IntroducingBrokerRebates'
import ConfirmationModal from 'components/ConfirmationModal/ConfirmationModal'
import { useAppSelector } from 'store'

import { SelectIBTradingAccount } from './SelectIBTradingAccount'
import { TradingAccountsPickerField } from './TradingAccountsPickerField'

interface Props {
  isOpen: boolean
  onClose(): void
  introducingBrokerId: string
  onSubmit: (data: IBIndividualRebate) => void | Promise<void>
}

interface RebateData {
  tradingAccounts: NameDto<string>[]
  approvalTypeId?: number
  periodId?: number
  rebateAmount: string
}

const initialData: RebateData = {
  tradingAccounts: [],
  approvalTypeId: undefined,
  periodId: undefined,
  rebateAmount: '',
}

const getTradingAccountsValue = (tradingAccounts: NameDto<string>[]) => {
  if (tradingAccounts.length === 0) return ''
  if (tradingAccounts.length <= 2) return tradingAccounts.map(({ name }) => name).join(', ')

  return `${tradingAccounts
    .slice(0, 2)
    .map(({ name }) => name)
    .join(', ')} +${tradingAccounts.length - 2} more`
}

export const AddRebateModal: React.FC<Props> = (props) => {
  const { introducingBrokerId, onSubmit, isOpen, onClose } = props
  const [data, setData] = useState<RebateData>(initialData)
  const [tradingAccountModal, setTradingAccountModal] = useState(false)
  const [confirmModal, setConfirmModal] = useState(false)

  useEffect(() => {
    if (!isOpen) return

    setData(initialData)
    setTradingAccountModal(false)
    setConfirmModal(false)
  }, [isOpen])

  const { ibRebatesApprovalTypes, ibRebatesPeriods } = useAppSelector(
    ({ partnerships }) => partnerships
  )

  const onSelectTradingAccount = (tradingAccount: TradingAccountDto, checked?: boolean) => {
    setData((current) => ({
      ...current,
      tradingAccounts: checked
        ? [...current.tradingAccounts, { name: tradingAccount.name, id: tradingAccount.id }]
        : current.tradingAccounts.filter(({ id }) => id !== tradingAccount.id),
    }))
  }

  const rebateAmountError = useMemo(() => {
    if (data.rebateAmount.trim() === '') return undefined

    const rebateAmount = Number(data.rebateAmount)

    if (Number.isNaN(rebateAmount)) {
      return 'Rebate amount must be a number.'
    }

    if (rebateAmount > 100) {
      return 'Rebate amount must be less than or equal to 100.'
    }

    if (rebateAmount < 0.1) {
      return 'Rebate amount must be greater than or equal to 0.1.'
    }

    return undefined
  }, [data.rebateAmount])

  const isValid = useMemo(
    () =>
      data.approvalTypeId !== undefined &&
      data.periodId !== undefined &&
      data.rebateAmount.trim() !== '' &&
      data.tradingAccounts.length > 0 &&
      !rebateAmountError,
    [data, rebateAmountError]
  )

  const handleSubmit = async () => {
    const rebateAmount = Number(data.rebateAmount)

    if (data.approvalTypeId === undefined) return
    if (data.periodId === undefined) return
    if (!data.tradingAccounts.length) return
    if (data.rebateAmount.trim() === '' || rebateAmountError) return
    if (Number.isNaN(rebateAmount)) return

    setConfirmModal(false)

    await onSubmit({
      rebateApprovalTypeId: data.approvalTypeId,
      rebatePeriodId: data.periodId,
      rebateAmountInPercentage: rebateAmount,
      isDefault: false,
      tradingAccountIds: data.tradingAccounts.map(({ id }) => id),
    })
  }

  return (
    <>
      <ConfirmationModal
        contentText='Are you sure you want to add the rebate?'
        yesButtonText='Yes'
        cancelButtonText='No'
        onYes={handleSubmit}
        onCancel={() => setConfirmModal(false)}
        visible={confirmModal}
      />

      <Dialog open={tradingAccountModal} onOpenChange={setTradingAccountModal}>
        <SelectIBTradingAccount
          onClose={() => setTradingAccountModal(false)}
          introducingBrokerId={introducingBrokerId}
          initialValue={data.tradingAccounts.map(({ id }) => id)}
          isMultiSelect
          onChange={onSelectTradingAccount}
        />
      </Dialog>

      <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
        <DialogContent size='sm'>
          <DialogHeader>
            <DialogTitle>Add Rebate For TA</DialogTitle>
          </DialogHeader>

          <TradingAccountsPickerField
            name='tradingAccounts'
            label='TA #'
            value={getTradingAccountsValue(data.tradingAccounts)}
            placeholder='Select trading account(s)'
            onSelect={() => setTradingAccountModal(true)}
          />
          <FormSelectField
            name='approvalTypeId'
            label='Type'
            options={ibRebatesApprovalTypes}
            value={data.approvalTypeId}
            onChange={(value) =>
              setData((current) => ({
                ...current,
                approvalTypeId: Number(value),
              }))
            }
          />
          <FormSelectField
            name='periodId'
            label='Payout period'
            options={ibRebatesPeriods}
            value={data.periodId}
            onChange={(value) =>
              setData((current) => ({
                ...current,
                periodId: Number(value),
              }))
            }
          />
          <FormField
            name='rebateAmount'
            label='Rebate amount, %'
            type='number'
            value={data.rebateAmount}
            error={rebateAmountError}
            onChange={(event) =>
              setData((current) => ({
                ...current,
                rebateAmount: event.target.value,
              }))
            }
          />

          <DialogFooter>
            <Button variant='outline' onClick={onClose}>
              Cancel
            </Button>
            <Button
              onClick={() => setConfirmModal(true)}
              disabled={!isValid}
              data-test='add-rebate-button'
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
